import { jwtDecode } from "jwt-decode";

/**
 * Whether an access token was issued by another login server than the one this build signs in with. The dev
 * environments moved from devauth.breecesystem.com to auth.breecelab.work on 27 Oct 2026, and a token the
 * browser kept from before is refused by every API (401) until it expires - with nothing on screen asking to
 * sign in again. A token that cannot be read is not judged: only a readable issuer that differs counts.
 */
export function issuedByAnotherAuthority(accessToken: string | null | undefined, authority: string): boolean {
  if (!accessToken) return false;
  try {
    const issuer = jwtDecode<{ iss?: string }>(accessToken).iss;
    return !!issuer && sameUrl(issuer) !== sameUrl(authority);
  } catch {
    return false;
  }
}

/** What the OIDC library keeps per configuration ('0-angulargui'): the parts that say which server it is from. */
interface StoredOidcSession {
  authWellKnownEndPoints?: { issuer?: string };
  authnResult?: { access_token?: string };
}

/**
 * Removes the sessions the OIDC library keeps in `storage` that belong to another login server - discovery
 * document, tokens and sign-in state together. Must run before the library loads its configuration: it lays a
 * stored discovery document over the one it fetches, so after the dev switch to auth.breecelab.work (27 Oct
 * 2026) the old server's issuer and addresses won and every sign-in stopped at "Issuer mismatch".
 * logoffLocal() clears the tokens, not that. Returns whether anything was removed.
 */
export function forgetSessionsFromAnotherAuthority(storage: Storage, authority: string): boolean {
  const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter((key): key is string => key !== null);
  let removed = false;
  for (const key of keys) {
    let session: StoredOidcSession | null;
    try {
      session = JSON.parse(storage.getItem(key) ?? '');
    } catch {
      continue; // not one of the library's entries
    }
    if (!session || typeof session !== 'object') continue;

    const issuer = session.authWellKnownEndPoints?.issuer;
    if ((issuer && sameUrl(issuer) !== sameUrl(authority)) || issuedByAnotherAuthority(session.authnResult?.access_token, authority)) {
      storage.removeItem(key);
      removed = true;
    }
  }
  return removed;
}

function sameUrl(url: string): string {
  return url.trim().replace(/\/+$/, '').toLowerCase();
}
